'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './menu-accordion.css';
const hamburgerIcon = "≡"; /* (U+2261) */ /* ||| */
const menuItemShape = PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string,
    target: PropTypes.string,
    routes: PropTypes.array, // Will be refined after function declaration
    hidden: PropTypes.bool,
});
// Update the recursive reference after the shape is defined
menuItemShape.routes = PropTypes.arrayOf(menuItemShape);
function generateMenuItems({ menuData, state = "hide" }) {
    const myItems = [];
    let index = 0;
    for (const itemKey in menuData) {
        const myItem = menuData[itemKey];
        const safeName = (myItem && myItem.name) ? String(myItem.name).replace(/\s+/g, '_') : 'item';
        const keyBase = `${safeName}-${itemKey}-${index}`;
        // if ( typeof myItem === 'object' && myItem !== null ){
        if (!myItem.hidden) {
            // ITEM OR GROUP IS NOT HIDDEN
            if (typeof myItem === 'object' && myItem.routes) {
                // MENU GROUP - push the toggle and the nested group separately with unique keys
                myItems.push(_jsx(MenuAccordionItem, { name: "▶ " + myItem.name, href: '' }, `${keyBase}-parent`), _jsx(MenuAccordionGroup, { menuItems: myItem, state: state }, `${keyBase}-group`));
            }
            else {
                // INDIVIDUAL MENU ITEM
                myItems.push(_jsx(MenuAccordionItem, { name: myItem.name, href: myItem.path, target: myItem.target }, `${keyBase}-item`));
            }
        }
        index++;
    }
    return myItems;
}
/**
 * MenuAccordion — Accordion-style navigational menu that supports nested menu groups and individual items.
 *
 * @param {arrayOf} [props.menuItems] - Array (or object) of menu items; items should include { name, path?, target?, hidden?, routes? }.
 * @param {boolean} [props.showHidden] - When true, items marked as `hidden` will be shown.
 */
MenuAccordion.propTypes = {
    /** Array of menu items (can contain nested `routes`) */
    menuItems: PropTypes.arrayOf(menuItemShape).isRequired,
    /** Show items marked `hidden` */
    showHidden: PropTypes.bool,
};
export function MenuAccordion(props) {
    const debug = false;
    const left = useRef(-250);
    function setLeft(leftVal) { left.current = leftVal; }
    ;
    const documentRef = useRef(null);
    // const [ menuItems, setMenuItems ] = useState();
    // only works for 2 layers deep
    const menuItems = props.menuItems.map((menuItem) => {
        // MAIN MENU ITEMS
        if (props.showHidden === true && menuItem.hidden === true) {
            delete menuItem.hidden;
        }
        // SUB MENU ITEMS
        if (menuItem.routes) {
            const subMenuItems = menuItem.routes.map((subMenuItem) => {
                if (props.showHidden === true && subMenuItem.hidden === true) {
                    delete subMenuItem.hidden;
                }
                return subMenuItem;
            });
            return { ...menuItem, routes: subMenuItems };
        }
        return menuItem;
    });
    function moveMenu() {
        if (debug)
            console.log("Moving Menu... Left: ", left);
        const menu = documentRef.current ? documentRef.current.getElementById('accordionMenu') : null;
        const menuParent = menu ? menu.parentElement : null;
        if (left.current === 0) {
            if (debug)
                console.log("Moving Menu Out");
            menuParent?.classList.remove('accordion-down'); /* accordionIn */
            menuParent?.classList.add('accordion-up'); /* accordionOut */
            setLeft(-250);
        }
        else {
            if (debug)
                console.log("Moving Menu In");
            menuParent?.classList.remove('accordion-up'); /* accordionOut */
            menuParent?.classList.add('accordion-down'); /* accordionIn */
            setLeft(0);
        }
    }
    ;
    function expandMenuItem(clickedItem) {
        if (debug)
            console.log("Expanding Menu Item...");
        const parent = clickedItem.parentElement;
        const subMenu = parent?.nextElementSibling;
        if (subMenu && subMenu.classList.contains('menu-hide')) {
            if (debug)
                console.log("Opening Submenu");
            subMenu.classList.add('menu-show');
            subMenu.classList.remove('menu-hide');
        }
        else {
            if (debug)
                console.log("Closing Submenu");
            if (subMenu)
                subMenu.classList.add('menu-hide');
            if (subMenu)
                subMenu.classList.remove('menu-show');
        }
    }
    useEffect(() => {
        window.moveMenu = moveMenu; // attach moveMenu function to the window object for use in MenuAccordionButton
        documentRef.current = document; // for moveMenu
        const menu = document.getElementById('accordionMenu');
        const menuBtn = document.getElementById('panel-menu-button');
        function handleMenuClick(event) {
            if (debug)
                console.log("event : ", event, "target : ", event.target);
            // const isClicked = (menu.contains(event.target) || menuBtn.contains(event.target));
            const isMenuClicked = menu?.contains(event.target);
            const isMenuBtnClicked = menuBtn?.contains(event.target);
            if (debug)
                console.log("isMenuBtnClicked : ", isMenuBtnClicked);
            if (isMenuClicked) {
                // MENU ITEM CLICKED
                const target = event.target;
                if (!(target.href) || !(target.href.length > 0)) {
                    // NO HREF - EXPAND / COLLAPSE SUB MENU
                    // event.preventDefault();
                    event.stopPropagation();
                    expandMenuItem(target);
                }
                else {
                    // HREF - NAVIGATE
                }
            }
            else if (isMenuBtnClicked) {
                // MENU BUTTON CLICKED
                // event.preventDefault();
                event.stopPropagation();
                moveMenu();
            }
            else if (!isMenuBtnClicked && left.current === 0) {
                // NON-MENU AREA CLICKED
                moveMenu();
            }
        }
        ;
        document.addEventListener('click', handleMenuClick, true);
        return () => {
            document.removeEventListener('click', handleMenuClick);
        };
    }, []);
    return (_jsx("div", { className: "accordion-menu-wrapper accordion-up", suppressHydrationWarning: true, children: _jsx("div", { className: "accordion-menu", id: "accordionMenu", children: _jsx(MenuAccordionGroup, { menuItems: menuItems, state: undefined }, "accordionRoot") }) }));
}
/* ========== MENU GROUP ========== */
/**
 * MenuAccordionGroup — Renders a group of menu items (may be a single item with `routes` or an array of items).
 *
 * @param {oneOfType} [props.menuItems] - A menu item object or an array of menu items (used for nested groups).
 * @param {string} [props.state] - Optional visibility state ('hide' or 'show').
 */
MenuAccordionGroup.propTypes = {
    /** Menu item or array of menu items (routes) */
    menuItems: PropTypes.oneOfType([
        menuItemShape,
        PropTypes.arrayOf(menuItemShape)
    ]).isRequired,
    /** Visibility state 'hide' | 'show' */
    state: PropTypes.string,
};
export function MenuAccordionGroup(props) {
    const myMenuItems = (props.menuItems.routes) ? props.menuItems.routes : props.menuItems;
    return (_jsx("ul", { className: (props.state === "hide" ? "menu-hide" : "menu-show"), children: generateMenuItems({ menuData: myMenuItems, state: props.state ?? "hide" }) }, "menu-group-" + props.menuItems.name));
}
/* ========== MENU ITEM ========== */
/**
 * MenuAccordionItem — Single menu entry used by the accordion.
 *
 * @param {string} [props.name] - Display text for the menu item.
 * @param {string} [props.href] - Destination href (empty string for expand/collapse toggles).
 * @param {string} [props.target] - Optional link target (e.g., '_self' or '_blank').
 */
MenuAccordionItem.propTypes = {
    /** Display text for the menu item */
    name: PropTypes.string.isRequired,
    /** Link href (or empty string for toggle items) */
    href: PropTypes.string.isRequired,
    /** Optional link target attribute */
    target: PropTypes.string,
};
export function MenuAccordionItem(props) {
    // Always render the same JSX structure to avoid hydration mismatch
    // href will be undefined or an empty string, target might be undefined
    return (_jsx("li", { children: _jsx("a", { href: props.href || undefined, target: props.target || undefined, children: props.name }) }, "menu-item-" + props.name));
}
/* ========== MENU BUTTON ========== */
/*
https://www.unclebigbay.com/blog/building-the-world-simplest-hamburger-with-html-and-css
*/
/** MenuAccordionButton — Mobile menu toggle button.
 * @param {any} [props] - No props are accepted by MenuAccordionButton.
 */
MenuAccordionButton.propTypes = { /** no props */};
export function MenuAccordionButton(props) {
    function slideMobilePanel() {
        if (typeof window !== 'undefined' && window.moveMenu) {
            window.moveMenu();
        }
    }
    // suppressHydrationWarning suppresses hydration mismatch warnings for this button
    return (_jsx("button", { className: "panel-menu-button", id: "panel-menu-button", onClick: slideMobilePanel, suppressHydrationWarning: true, children: _jsx("span", { className: "hamburger text-outline", children: hamburgerIcon }) }));
}
